import { FC, useEffect } from 'react'
import { Modal } from '../Atoms/Modal'
import {
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { PrimaryButton } from '../Atoms/Buttons'
import LoadingIcon from '../LoadingIcon'
import { ConfirmModalStatus } from '../Preshipment/ModalSuccess'
import logo from '../../assets/images/TealcaLogo.png'

interface ModalLoadingProps {
  open: boolean
  title: string
  status: ConfirmModalStatus
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  onPrint?: () => void
  timeOut?: number
  actionLabel?: string
  onAction?: () => void
}

export const ModalLoading: FC<ModalLoadingProps> = ({
  open,
  title,
  setOpen,
  status,
  onPrint,
  timeOut = 15000,
  actionLabel,
  onAction,
}) => {
  useEffect(() => {
    if (!open) return

    const shouldAutoClose = [
      ConfirmModalStatus.ERROR,
      ConfirmModalStatus.SUCCESS,
      ConfirmModalStatus.WARNING,
    ].includes(status)

    // Si hay acción definida, no auto-cerrar inmediatamente para permitir interacción
    if (shouldAutoClose && !onAction) {
      const timer = setTimeout(() => {
        setOpen(false)
      }, timeOut)
      return () => clearTimeout(timer)
    }
  }, [open, status, timeOut, setOpen, onAction])

  const handleClose = () => {
    if (status !== ConfirmModalStatus.PROCESSING) {
      setOpen(false)
    }
  }

  const showCloseButton = status !== ConfirmModalStatus.PROCESSING

  // Calcula el tamaño basado en el contenido
  const getModalSize = () => {
    // Divide el título por líneas (considerando saltos de línea reales)
    const lines = title.split('\n')
    const lineCount = lines.length
    const maxLineLength = Math.max(...lines.map((line) => line.length))

    // Ajusta el ancho basado en la línea más larga
    const widthClass = maxLineLength > 50 ? 'sm:max-w-md' : 'sm:max-w-xs'

    // Ajusta la altura basada en la cantidad de líneas
    const baseHeight = Math.max(4, lineCount + 2) // Mínimo 4rem

    return {
      width: widthClass,
      height: `min-h-[${baseHeight}rem]`,
    }
  }

  const { width, height } = getModalSize()

  // Renderiza el título conservando los saltos de línea
  const renderTitle = () => {
    return title.split('\n').map((line, i) => (
      <span key={i}>
        {line}
        {i < title.split('\n').length - 1 && <br />}
      </span>
    ))
  }

  return (
    <Modal
      open={open}
      setOpen={handleClose}
      className={`${width} w-full max-w-[95vw] ${height}`}
    >
      <div className="relative flex flex-col items-center p-4 w-full">
        {/* Botón de cierre (X) */}
        {showCloseButton && (
          <button
            onClick={handleClose}
            className={`absolute top-2 right-2 p-1 rounded-full transition-all 
            ${
              status === ConfirmModalStatus.ERROR
                ? 'hover:bg-red-50 active:bg-red-100'
                : status === ConfirmModalStatus.SUCCESS
                ? 'hover:bg-green-50 active:bg-green-100'
                : status === ConfirmModalStatus.WARNING
                ? 'hover:bg-yellow-50 active:bg-yellow-100'
                : 'hover:bg-gray-50 active:bg-gray-100'
            }`}
            aria-label="Cerrar modal"
          >
            <XMarkIcon
              className={`w-5 h-5 animate-pulse ${
                status === ConfirmModalStatus.ERROR
                  ? 'text-red-600 hover:text-red-700'
                  : status === ConfirmModalStatus.SUCCESS
                  ? 'text-green-600 hover:text-green-700'
                  : status === ConfirmModalStatus.WARNING
                  ? 'text-yellow-500 hover:text-yellow-600'
                  : 'text-gray-500 hover:text-gray-600'
              } transition-colors`}
            />
          </button>
        )}

        {/* Contenido según estado */}
        <div className="flex flex-col items-center justify-center w-full space-y-3">
          {status === ConfirmModalStatus.ERROR && (
            <>
              <XCircleIcon className="text-red-600 w-12 h-12" />
              <p className="text-center text-red-600 font-bold break-words px-2 whitespace-pre-line">
                {renderTitle()}
              </p>
            </>
          )}

          {status === ConfirmModalStatus.SUCCESS && (
            <>
              <CheckCircleIcon className="text-green-600 w-12 h-12" />
              <p className="text-center font-bold break-words px-2 whitespace-pre-line">
                {renderTitle()}
              </p>
              {(actionLabel || onAction) && (
                <div className="flex flex-wrap justify-center gap-2 w-full pt-2">
                  {actionLabel && onAction && (
                    <PrimaryButton
                      onClick={() => onAction?.()}
                      className="px-3 py-1"
                    >
                      {actionLabel}
                    </PrimaryButton>
                  )}
                  <PrimaryButton onClick={handleClose} className="px-3 py-1">
                    Cerrar
                  </PrimaryButton>
                </div>
              )}
            </>
          )}

          {status === ConfirmModalStatus.PRINT && (
            <>
              <CheckCircleIcon className="text-green-600 w-12 h-12" />
              <p className="text-center font-bold break-words px-2 whitespace-pre-line">
                {renderTitle()}
              </p>
              <div className="flex flex-wrap justify-center gap-2 w-full pt-2">
                <PrimaryButton
                  onClick={() => onPrint?.()}
                  className="px-3 py-1"
                >
                  Imprimir
                </PrimaryButton>
                <PrimaryButton onClick={handleClose} className="px-3 py-1">
                  Cerrar
                </PrimaryButton>
              </div>
            </>
          )}

          {status === ConfirmModalStatus.WARNING && (
            <>
              <CheckCircleIcon className="text-yellow-500 w-12 h-12" />
              <p className="text-center font-bold break-words px-2 whitespace-pre-line">
                {renderTitle()}
              </p>
              <div className="pt-2">
                <PrimaryButton onClick={handleClose} className="px-3 py-1">
                  Cerrar
                </PrimaryButton>
              </div>
            </>
          )}

          {status === ConfirmModalStatus.PROCESSING && (
            <div className="flex flex-col items-center">
              <div className="relative inline-flex items-center justify-center">
                <LoadingIcon size="8rem" /> {/* spinner */}
                <img
                  src={logo}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-auto animate-pulse"
                  alt="Tealca logo"
                />
              </div>
              <p className="text-center font-bold break-words px-2 mt-4 animate-pulse whitespace-pre-line">
                {renderTitle()}
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ModalLoading
